import {
  buildEditingModeHref,
  DEFAULT_EDITING_MODE,
  EDITING_MODE_DISPLAY_ORDER,
  type EditingMode,
} from '@/app/constants';
import type { LiveScene } from '@/app/types';

/**
 * 参考图语义，沿用 Xmax 的四档：
 * - `required`：必须有参考图，模式才能跑
 * - `optional`：参考图可有可无
 * - `none`：该模式不消费参考图
 * - `flexible`：参考图可选，且允许取消选择
 */
export type RefImageBehavior = 'required' | 'optional' | 'none' | 'flexible';

export type EditingMaterialKind =
  | 'style-reference'
  | 'character-reference'
  | 'background-reference'
  | 'clothing-reference';

export type EditingBuiltInAsset = {
  /** 前端素材标识，接口素材直接使用后端返回的 id。 */
  id: string;
  editingAssetId?: string;
  url: string;
  nameKey?: string;
  name?: string;
  exampleVideoUri?: string;
};

export type EditingModeEntry = {
  id: EditingMode;
  /** 该模式的深链地址（单路由下是 /vidu-stream/editing 或带 ?mode= 的深链） */
  href: string;
  /** i18n key，解析出模式展示名 */
  nameKey: string;
  /** i18n key，解析出模式说明文案 */
  descriptionKey: string;
  /**
   * 对应模式的默认编辑意图。当前协议尚未提供直播中更新编辑参数的指令，
   * 先作为类型化配置保留，接入编辑指令 adapter 后由 adapter 消费。
   */
  defaultPrompt: string;
  refImageBehavior: RefImageBehavior;
  materialKind: EditingMaterialKind;
  builtInAssets: readonly EditingBuiltInAsset[];
  reportElementName: string;
  /**
   * PV 埋点 `stream_editing_type` 的取值。数据侧按中文字面值统计，
   * 因此写死中文常量、不接 i18n——否则英文站会上报另一套值，同一个模式在报表里裂成两行。
   */
  reportStreamEditingType: string;
  liveScene: LiveScene;
};

const EMPTY_BUILT_IN_ASSETS: readonly EditingBuiltInAsset[] = [];

/**
 * 模式注册表。热插拔方式：直接从数组里删掉某一项，UI 会自动跟随，
 * 不需要改其它文件。顺序即展示顺序。
 *
 * 内置素材只来自 keeper `/live/v1/keeper/editing-assets`；注册表本身不预置静态兜底。
 */
const EDITING_MODE_ENTRY_BY_ID: Record<EditingMode, EditingModeEntry> = {
  'style-rendering': {
    id: 'style-rendering',
    href: buildEditingModeHref('style-rendering'),
    nameKey: 'modes.styleRendering.name',
    descriptionKey: 'modes.styleRendering.description',
    defaultPrompt: '将输入画面实时渲染为参考图的视觉风格',
    refImageBehavior: 'required',
    materialKind: 'style-reference',
    builtInAssets: EMPTY_BUILT_IN_ASSETS,
    reportElementName: 'styleRendering',
    reportStreamEditingType: '风格渲染',
    liveScene: 'style_transfer',
  },
  'character-replacement': {
    id: 'character-replacement',
    href: buildEditingModeHref('character-replacement'),
    nameKey: 'modes.characterReplacement.name',
    descriptionKey: 'modes.characterReplacement.description',
    defaultPrompt: '将输入画面中的人物替换为参考角色',
    refImageBehavior: 'required',
    materialKind: 'character-reference',
    builtInAssets: EMPTY_BUILT_IN_ASSETS,
    reportElementName: 'characterReplacement',
    reportStreamEditingType: '角色替换',
    liveScene: 'subject_replacement',
  },
  'background-replacement': {
    id: 'background-replacement',
    href: buildEditingModeHref('background-replacement'),
    nameKey: 'modes.backgroundReplacement.name',
    descriptionKey: 'modes.backgroundReplacement.description',
    defaultPrompt: '将输入画面的背景替换为参考背景',
    refImageBehavior: 'required',
    materialKind: 'background-reference',
    builtInAssets: EMPTY_BUILT_IN_ASSETS,
    reportElementName: 'backgroundReplacement',
    reportStreamEditingType: '背景替换',
    liveScene: 'background_replacement',
  },
  'virtual-try-on': {
    id: 'virtual-try-on',
    href: buildEditingModeHref('virtual-try-on'),
    nameKey: 'modes.virtualTryOn.name',
    descriptionKey: 'modes.virtualTryOn.description',
    defaultPrompt: '将输入画面中人物的服装替换为参考服装',
    refImageBehavior: 'required',
    materialKind: 'clothing-reference',
    builtInAssets: EMPTY_BUILT_IN_ASSETS,
    reportElementName: 'virtualTryOn',
    reportStreamEditingType: '虚拟换衣',
    liveScene: 'virtual_tryon',
  },
};

export const EDITING_MODE_ENTRIES: EditingModeEntry[] = EDITING_MODE_DISPLAY_ORDER.map(
  (mode) => EDITING_MODE_ENTRY_BY_ID[mode],
);

/** 将 keeper 接口返回的素材映射为编辑页结构；某个 scene 没数据时 builtInAssets 为空，不回落静态素材。 */
export const mapEditingAssetsToModes = (
  assets: readonly {
    id?: string;
    scene?: string;
    reference_image_uris?: readonly string[];
    name?: string;
    example_video_uri?: string;
  }[],
  modes: readonly EditingModeEntry[] = EDITING_MODE_ENTRIES,
): EditingModeEntry[] => {
  const byScene = new Map<string, Array<(typeof assets)[number]>>();
  for (const asset of assets) {
    const uri = asset.reference_image_uris?.[0];
    if (!asset.id || !asset.scene || !uri) continue;
    const group = byScene.get(asset.scene) ?? [];
    group.push(asset);
    byScene.set(asset.scene, group);
  }

  return modes.map((entry) => {
    const remote = byScene.get(entry.liveScene) ?? [];
    return {
      ...entry,
      builtInAssets: remote.map((asset) => ({
        id: asset.id as string,
        editingAssetId: asset.id,
        url: asset.reference_image_uris?.[0] ?? '',
        name: asset.name || undefined,
        exampleVideoUri: asset.example_video_uri,
      })),
    };
  });
};

/** 按 id 取模式配置；模式被摘掉后返回 undefined，调用方需自行兜底。 */
export const findEditingMode = (id: EditingMode): EditingModeEntry | undefined =>
  EDITING_MODE_ENTRIES.find((entry) => entry.id === id);

/** 取业务默认模式；展示顺序调整不应改变页面初始化模式。 */
export const getDefaultEditingMode = (): EditingModeEntry | undefined => findEditingMode(DEFAULT_EDITING_MODE);

/**
 * 取 PV 埋点用的编辑类型。走全量表而不是 EDITING_MODE_ENTRIES.find，
 * 展示顺序里摘掉某个模式时也不会上报空值。
 */
export const resolveStreamEditingType = (mode: EditingMode): string =>
  EDITING_MODE_ENTRY_BY_ID[mode]?.reportStreamEditingType ??
  EDITING_MODE_ENTRY_BY_ID[DEFAULT_EDITING_MODE].reportStreamEditingType;
